/**
 * The consumer API's paths, limits and shapes (QZ-D120). Its routes are server-to-server: they
 * read approved tasks (QZ-D24) and take task feedback, its one write (QZ-D150).
 */

import { z } from 'zod';
import { jobConfigIdSchema, jobStatusSchema, jobTaskCountSchema } from '../job/job';
import { tagNameSchema } from '../tag/tag';
import {
  ageGroupSchema,
  ageGroupsSchema,
  difficultySchema,
  languageSchema,
  numericAnswerSchema,
  taskIdSchema,
  taskTypeSchema,
  taskTypesSchema,
  tagsSchema,
  textSchema,
  timestampSchema,
} from '../task/task';

/** The random-tasks path (QZ-D120). */
export const CONSUMER_TASKS_PATH = '/consumer/tasks';

/** The document-search path (QZ-D120). */
export const CONSUMER_DOCUMENTS_PATH = '/consumer/documents';

/** The tag-list path (QZ-D120). */
export const CONSUMER_TAGS_PATH = '/consumer/tags';

/** `GET /consumer/tasks`'s default `count` when omitted (QZ-D122). */
export const CONSUMER_TASK_COUNT_DEFAULT = 10;

/** The most tasks `GET /consumer/tasks` answers; GPS Quiz's most tasks per quiz (QZ-D122). */
export const CONSUMER_TASK_COUNT_MAX = 30;

/** The most task ids `exclude` may list (QZ-D125). */
export const CONSUMER_EXCLUDE_MAX = 100;

/** The most tag names `tags` may list (TMA-D4). */
export const CONSUMER_TAGS_MAX = 50;

/** The shortest `search` that `GET /consumer/documents` accepts (QZ-D126). */
export const CONSUMER_SEARCH_MIN_LENGTH = 2;

/** The most documents `GET /consumer/documents` answers (QZ-D126). */
export const CONSUMER_DOCUMENT_HITS_MAX = 20;

/**
 * The query string accepted by `GET /consumer/tasks` (QZ-D122, QZ-D125, QZ-D132, TMA-D4). Unlike
 * `listTasksQuerySchema`, nothing calls `.omit` on this schema, so a `superRefine` is fine here.
 */
export const consumerTasksQuerySchema = z
  .object({
    language: languageSchema,
    count: z.coerce
      .number()
      .int()
      .min(1)
      .max(CONSUMER_TASK_COUNT_MAX)
      .default(CONSUMER_TASK_COUNT_DEFAULT),
    // A comma-separated list of tag names, any of which matches (QZ-D132). Absent parses to
    // undefined; an empty or all-blank value parses to [], which filters nothing, like `exclude`.
    // `.optional()` comes last so the key stays optional in the parsed type.
    tags: z
      .string()
      .transform((raw, ctx) => {
        const names: string[] = [];
        for (const part of raw.split(',')) {
          const trimmed = part.trim();
          if (trimmed === '') {
            continue;
          }
          const parsed = tagNameSchema.safeParse(trimmed);
          if (!parsed.success) {
            ctx.addIssue({ code: 'custom', message: `Not a tag name: ${trimmed}` });
            return z.NEVER;
          }
          if (names.includes(parsed.data)) {
            ctx.addIssue({ code: 'custom', message: 'A tag is listed more than once.' });
            return z.NEVER;
          }
          names.push(parsed.data);
        }
        if (names.length > CONSUMER_TAGS_MAX) {
          ctx.addIssue({ code: 'custom', message: `At most ${CONSUMER_TAGS_MAX} tags.` });
          return z.NEVER;
        }
        return names;
      })
      .optional(),
    ageGroup: ageGroupSchema.optional(),
    taskType: taskTypeSchema.optional(),
    difficulty: difficultySchema.optional(),
    document: textSchema.max(200).optional(),
    section: textSchema.max(200).optional(),
    // A comma-separated list of task ids to leave out (QZ-D125); absent means none.
    exclude: z
      .string()
      .optional()
      .transform((raw, ctx) => {
        if (raw === undefined) {
          return [] as string[];
        }
        const ids: string[] = [];
        for (const part of raw.split(',')) {
          const trimmed = part.trim();
          if (trimmed === '') {
            continue;
          }
          const parsed = taskIdSchema.safeParse(trimmed);
          if (!parsed.success) {
            ctx.addIssue({ code: 'custom', message: `Not a task id: ${trimmed}` });
            return z.NEVER;
          }
          ids.push(parsed.data);
        }
        if (ids.length > CONSUMER_EXCLUDE_MAX) {
          ctx.addIssue({ code: 'custom', message: `At most ${CONSUMER_EXCLUDE_MAX} task ids.` });
          return z.NEVER;
        }
        return ids;
      }),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.section !== undefined && value.document === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['section'],
        message: 'A section needs a document.',
      });
    }
  });
export type ConsumerTasksQuery = z.output<typeof consumerTasksQuerySchema>;

/** One multiple-choice option on a consumer task (QZ-D124). */
export const consumerChoiceSchema = z
  .object({
    text: textSchema,
    correct: z.boolean(),
  })
  .strip();
export type ConsumerChoice = z.infer<typeof consumerChoiceSchema>;

/** Where a consumer task's content came from (QZ-D124); absent for an open-web task. No `kind`. */
export const consumerSourceSchema = z.union([
  z
    .object({
      document: textSchema,
      section: textSchema.max(200).optional(),
      page: z.number().int().positive().optional(),
    })
    .strict(),
  z
    .object({
      url: z.url({ protocol: /^https?$/ }),
    })
    .strict(),
]);
export type ConsumerSource = z.infer<typeof consumerSourceSchema>;

/**
 * One task as a consumer receives it (QZ-D124): one language's wording and its answer data,
 * with the choices shuffled and marked, and everything review-only left out.
 */
export const consumerTaskSchema = z
  .object({
    taskId: taskIdSchema,
    taskTypes: taskTypesSchema,
    difficulty: difficultySchema,
    tags: tagsSchema,
    ageGroups: ageGroupsSchema,
    language: languageSchema,
    text: textSchema,
    choices: z.array(consumerChoiceSchema).min(2).max(6).optional(),
    freetextAnswer: textSchema.max(60).optional(),
    items: z.array(textSchema).min(2).max(6).optional(),
    numericAnswer: numericAnswerSchema.optional(),
    source: consumerSourceSchema.optional(),
  })
  .strip()
  .superRefine((task, ctx) => {
    if (task.choices !== undefined) {
      const correctCount = task.choices.filter((choice) => choice.correct).length;
      if (correctCount !== 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['choices'],
          message: 'Exactly one choice is correct.',
        });
      }
    }
  });
export type ConsumerTask = z.infer<typeof consumerTaskSchema>;

/** The body returned by `GET /consumer/tasks` (QZ-D122). */
export const consumerTasksResponseSchema = z
  .object({
    tasks: z.array(consumerTaskSchema).max(CONSUMER_TASK_COUNT_MAX),
  })
  .strip();
export type ConsumerTasksResponse = z.infer<typeof consumerTasksResponseSchema>;

/** The query string accepted by `GET /consumer/documents` (QZ-D126). */
export const consumerDocumentsQuerySchema = z
  .object({
    search: z.string().trim().min(CONSUMER_SEARCH_MIN_LENGTH).max(200),
  })
  .strict();
export type ConsumerDocumentsQuery = z.infer<typeof consumerDocumentsQuerySchema>;

/** One document's section as a consumer receives it (QZ-D126); only sections with approved tasks. */
export const consumerSectionSchema = z
  .object({
    name: textSchema,
    approved: z.number().int().min(1),
  })
  .strip();
export type ConsumerSection = z.infer<typeof consumerSectionSchema>;

/** One document as a consumer receives it (QZ-D126); only documents with approved tasks. */
export const consumerDocumentSchema = z
  .object({
    document: textSchema,
    approved: z.number().int().min(1),
    sections: z.array(consumerSectionSchema),
  })
  .strip();
export type ConsumerDocument = z.infer<typeof consumerDocumentSchema>;

/**
 * The body returned by `GET /consumer/documents` (QZ-D126): hits ordered by
 * `compareSectionNames` on `document`, each one's `sections` ordered by `sortSections`, as
 * `listDocumentsResponseSchema` says. The schema itself does not sort or check the order.
 */
export const consumerDocumentsResponseSchema = z
  .object({
    documents: z.array(consumerDocumentSchema).max(CONSUMER_DOCUMENT_HITS_MAX),
  })
  .strip();
export type ConsumerDocumentsResponse = z.infer<typeof consumerDocumentsResponseSchema>;

/** The task-feedback path (QZ-D151). */
export const CONSUMER_FEEDBACK_PATH = '/consumer/feedback';

/** The most feedback entries one call to `POST /consumer/feedback` may carry (QZ-D151). */
export const CONSUMER_FEEDBACK_MAX = 30;

/** The longest a declined result's reason may be, after trimming (QZ-D151). */
export const DECLINE_REASON_MAX = 200;

/** A task feedback result: used in a published quiz walk, or declined (QZ-D151). */
export const FEEDBACK_RESULTS = ['used', 'declined'] as const;
export type FeedbackResult = (typeof FEEDBACK_RESULTS)[number];

/**
 * The body of `POST /consumer/feedback`: one language and 1–30 results (QZ-D150, QZ-D151).
 */
export const consumerFeedbackRequestSchema = z
  .object({
    language: languageSchema,
    feedback: z
      .array(
        z
          .object({
            taskId: taskIdSchema,
            result: z.enum(FEEDBACK_RESULTS),
            reason: textSchema.max(DECLINE_REASON_MAX).optional(),
          })
          .strict()
      )
      .min(1)
      .max(CONSUMER_FEEDBACK_MAX),
  })
  .strict()
  .superRefine((value, ctx) => {
    const seenTaskIds = new Set<string>();
    value.feedback.forEach((entry, i) => {
      if (entry.result === 'used' && entry.reason !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['feedback', i, 'reason'],
          message: 'Only a declined result may have a reason.',
        });
      }
      if (seenTaskIds.has(entry.taskId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['feedback', i, 'taskId'],
          message: 'A task is listed more than once.',
        });
      }
      seenTaskIds.add(entry.taskId);
    });
  });
export type ConsumerFeedbackRequest = z.infer<typeof consumerFeedbackRequestSchema>;

/** The user jobs collection path (QZ-D198, QZ-D199). */
export const CONSUMER_JOBS_PATH = '/consumer/jobs';

/** The most pages a user job's request may carry (QZ-D198). */
export const USER_JOB_PAGES_MAX = 10;

/** The most characters a user job's request may carry, summed over its pages (QZ-D198). */
export const USER_JOB_CHARS_MAX = 50_000;

/** The number of user jobs returned by one page of `GET /consumer/jobs` (QZ-D199). */
export const USER_JOB_PAGE_SIZE = 25;

/** The generation method a user job's request may name (QZ-D198). */
export const userJobMethodSchema = z.enum(['pdf', 'photos']);
export type UserJobMethod = z.infer<typeof userJobMethodSchema>;

/** One page of a user job's request: a PDF page as the PDF numbers it, or a photo number. */
export const userJobPageSchema = z
  .object({
    page: z.number().int().min(1),
    text: z.string(),
  })
  .strict();
export type UserJobPage = z.infer<typeof userJobPageSchema>;

/**
 * The body of `POST /consumer/jobs` (QZ-D198): its text carried inline, at most
 * `USER_JOB_PAGES_MAX` pages and `USER_JOB_CHARS_MAX` characters in total.
 */
export const createUserJobRequestSchema = z
  .object({
    jobConfig: jobConfigIdSchema,
    method: userJobMethodSchema,
    document: textSchema.max(200),
    language: languageSchema,
    taskCount: jobTaskCountSchema,
    pages: z.array(userJobPageSchema).min(1).max(USER_JOB_PAGES_MAX),
  })
  .strict()
  .superRefine((value, ctx) => {
    for (let i = 1; i < value.pages.length; i++) {
      if (value.pages[i].page <= value.pages[i - 1].page) {
        ctx.addIssue({
          code: 'custom',
          path: ['pages', i, 'page'],
          message: 'Pages must be numbered, unique and strictly ascending.',
        });
      }
    }
    const totalChars = value.pages.reduce((sum, page) => sum + page.text.length, 0);
    if (totalChars > USER_JOB_CHARS_MAX) {
      ctx.addIssue({
        code: 'custom',
        path: ['pages'],
        message: `At most ${USER_JOB_CHARS_MAX.toLocaleString('en-US')} characters in total.`,
      });
    }
  });
export type CreateUserJobRequest = z.infer<typeof createUserJobRequestSchema>;

/** A user job as its own list entry (QZ-D199). */
export const userJobSummarySchema = z
  .object({
    jobId: taskIdSchema,
    document: textSchema.max(200),
    method: userJobMethodSchema,
    language: languageSchema,
    status: jobStatusSchema,
    /** A whole percentage: 0 while planning, 100 once ended (QZ-D199). */
    progress: z.number().int().min(0).max(100),
    taskCount: jobTaskCountSchema,
    createdAt: timestampSchema,
  })
  .strip();
export type UserJobSummary = z.infer<typeof userJobSummarySchema>;

/** The query string accepted by `GET /consumer/jobs` (QZ-D199). */
export const listUserJobsQuerySchema = z.object({ cursor: z.string().min(1).optional() }).strict();
export type ListUserJobsQuery = z.infer<typeof listUserJobsQuerySchema>;

/** The body returned by `GET /consumer/jobs` (QZ-D199). */
export const listUserJobsResponseSchema = z
  .object({
    jobs: z.array(userJobSummarySchema),
    cursor: z.string().min(1).optional(),
  })
  .strip();
export type ListUserJobsResponse = z.infer<typeof listUserJobsResponseSchema>;

/** The body returned by `GET /consumer/jobs/:jobId` (QZ-D199). */
export const userJobResponseSchema = userJobSummarySchema.extend({
  error: z.string().max(500).optional(),
  updatedAt: timestampSchema,
  tasks: z.array(consumerTaskSchema),
});
export type UserJobResponse = z.infer<typeof userJobResponseSchema>;
